import { Modal, Form, Input, DatePicker, Switch, Select, Typography, App } from 'antd'
import { useState, useEffect } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { updateSurvey } from '../services/survey-config-api'
import { useTenants } from '../../tenants/hooks/useTenants'
import { MarkdownPreview } from '../../announcements/components/MarkdownPreview'
import type { SurveyConfig } from '../types/survey.types'

interface EditSurveyModalProps {
  open: boolean
  survey: SurveyConfig | null
  onClose: () => void
  onSuccess: () => void
}

interface FormValues {
  title: string
  description: string
  scope: 'global' | 'specific'
  targetTenantCodes: string[]
  startDate: Dayjs
  endDate: Dayjs
  isActive: boolean
}

export function EditSurveyModal({ open, survey, onClose, onSuccess }: EditSurveyModalProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const [loading, setLoading] = useState(false)
  const { tenants } = useTenants({ includeInactive: true })
  const scope = Form.useWatch('scope', form)
  const descriptionValue = Form.useWatch('description', form)

  useEffect(() => {
    if (open && survey) {
      let tenantCodes: string[] = []
      try {
        if (survey.targetTenantCodes) {
          tenantCodes = JSON.parse(survey.targetTenantCodes)
        }
      } catch {
        tenantCodes = []
      }

      form.setFieldsValue({
        title: survey.title,
        description: survey.description || '',
        scope: survey.targetTenantCodes ? 'specific' : 'global',
        targetTenantCodes: tenantCodes,
        startDate: dayjs(survey.startDate),
        endDate: dayjs(survey.endDate),
        isActive: survey.isActive
      })
    }
  }, [open, survey, form])

  const handleSubmit = async (values: FormValues) => {
    if (!survey) return

    try {
      setLoading(true)
      await updateSurvey(survey.id, {
        title: values.title,
        description: values.description || null,
        targetTenantCodes: values.scope === 'global' ? null : JSON.stringify(values.targetTenantCodes),
        startDate: values.startDate.toISOString(),
        endDate: values.endDate.toISOString(),
        isActive: values.isActive
      })
      message.success('Survey updated successfully')
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error updating survey:', error)
      message.error(error.response?.data?.message || 'Failed to update survey')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const tenantOptions = tenants.map(t => ({
    label: `${t.tenantName} (${t.tenantCode})`,
    value: t.tenantCode
  }))

  return (
    <Modal
      title="Edit survey"
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText="Save"
      confirmLoading={loading}
      width={600}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, message: 'Please enter title' },
            { max: 200, message: 'Title must not exceed 200 characters' }
          ]}
        >
          <Input placeholder="e.g., Digitalization Survey 2025" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Description{' '}
              <Typography.Text type="secondary" style={{ fontWeight: 'normal', fontSize: '12px' }}>
                (markdown: ### heading, **bold**, *italic*, {'>'} note, [link](url))
              </Typography.Text>
            </span>
          }
          name="description"
        >
          <Input.TextArea rows={3} placeholder="Optional description" />
        </Form.Item>

        {descriptionValue && (
          <div
            style={{
              marginTop: -8,
              marginBottom: 16,
              padding: '8px 12px',
              background: '#fafafa',
              borderRadius: 6,
              border: '1px solid #d9d9d9',
              minHeight: 40
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              Preview:
            </Typography.Text>
            <MarkdownPreview content={descriptionValue} />
          </div>
        )}

        <Form.Item label="Scope" name="scope" rules={[{ required: true }]}>
          <Select
            options={[
              { label: 'All tenants', value: 'global' },
              { label: 'Specific tenants', value: 'specific' }
            ]}
          />
        </Form.Item>

        {scope === 'specific' && (
          <Form.Item
            label="Target tenants"
            name="targetTenantCodes"
            rules={[{ required: scope === 'specific', message: 'Please select at least one tenant' }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Select tenants"
              options={tenantOptions}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            label="Start date"
            name="startDate"
            rules={[{ required: true, message: 'Please select start date' }]}
          >
            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          <Form.Item
            label="End date"
            name="endDate"
            rules={[
              { required: true, message: 'Please select end date' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || !getFieldValue('startDate')) {
                    return Promise.resolve()
                  }
                  if (value.isAfter(getFieldValue('startDate'))) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('End date must be after start date'))
                }
              })
            ]}
          >
            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
          </Form.Item>
        </div>

        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
